import React, { useCallback } from 'react';

// Recreated Polymet-style MapTable with local UI (no external libs)
// Props contract:
// - items, selectedItems, onSelectionChange, onItemUpdate, onItemClick
// - onBulkAddToSprint, onBulkRemoveFromSprint, onBulkMarkCompleted
// - sortField, sortDirection, onSortChange
const MapTable = ({
  items = [],
  selectedItems = [],
  onSelectionChange,
  onItemUpdate,
  onItemClick,
  onBulkAddToSprint,
  onBulkRemoveFromSprint,
  onBulkMarkCompleted,
  sortField,
  sortDirection,
  onSortChange,
  className = ''
}) => {
  const getDomainClasses = (domain) => {
    return domain === 'business'
      ? 'bg-succession-success-bg text-succession-success border-emerald-200'
      : 'bg-succession-primary-10 text-succession-primary border-blue-200';
  };

  const getVmiClasses = (vmi) => {
    switch (vmi) {
      case 'identify':
        return 'bg-succession-primary-10 text-succession-primary border-blue-200';
      case 'protect':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'build':
        return 'bg-purple-50 text-purple-700 border-purple-200';
      case 'harvest':
        return 'bg-orange-50 text-orange-700 border-orange-200';
      case 'manage':
        return 'bg-succession-bg-secondary text-succession-text-secondary border-gray-200';
      default:
        return 'bg-succession-bg-secondary text-succession-text-secondary border-gray-200';
    }
  };

  const startCase = (val) => (val ? val.charAt(0).toUpperCase() + val.slice(1) : '—');
  const humanize = (val) => {
    if (!val) return '—';
    const map = { wont_do: "Won't Do" };
    if (map[val]) return map[val];
    return String(val)
      .split('_')
      .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
      .join(' ');
  };
  const toggleAll = useCallback(() => {
    if (!onSelectionChange) return;
    if (selectedItems.length === items.length) onSelectionChange([]);
    else onSelectionChange(items.map(i => i.id));
  }, [items, selectedItems, onSelectionChange]);

  const toggleOne = useCallback((id) => {
    if (!onSelectionChange) return;
    if (selectedItems.includes(id)) onSelectionChange(selectedItems.filter(x => x !== id));
    else onSelectionChange([...selectedItems, id]);
  }, [selectedItems, onSelectionChange]);

  const handleSort = (field) => {
    const dir = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    onSortChange && onSortChange(field, dir);
  };

  return (
    <div className={`bg-succession-bg-primary rounded-lg border border-succession-border shadow-sm overflow-hidden ${className}`}>
      {selectedItems.length > 0 && (
        <div className="bg-succession-primary-10 border-b border-succession-border px-4 py-3 flex items-center justify-between">
          <div className="text-sm text-succession-text-primary">{selectedItems.length} selected</div>
          <div className="flex items-center gap-2">
            <button className="text-xs px-3 py-1 border border-succession-border rounded text-succession-text-primary" onClick={() => onBulkAddToSprint && onBulkAddToSprint(selectedItems)}>Add to Sprint</button>
            <button className="text-xs px-3 py-1 border border-succession-border rounded text-succession-text-primary" onClick={() => onBulkRemoveFromSprint && onBulkRemoveFromSprint(selectedItems)}>Remove</button>
            <button className="text-xs px-3 py-1 border border-succession-border rounded text-succession-text-primary" onClick={() => onBulkMarkCompleted && onBulkMarkCompleted(selectedItems)}>Mark Completed</button>
            <button className="text-xs px-3 py-1 text-succession-text-secondary" onClick={() => onSelectionChange && onSelectionChange([])}>Clear</button>
          </div>
        </div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-succession-bg-secondary/70 border-b border-succession-border">
            <tr>
              <th className="px-4 py-2 w-10"><input type="checkbox" checked={selectedItems.length === items.length && items.length > 0} onChange={toggleAll} /></th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider cursor-pointer" onClick={() => handleSort('title')}>Title</th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider cursor-pointer" onClick={() => handleSort('domain')}>Domain</th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider cursor-pointer" onClick={() => handleSort('cepaCategory')}>VMI Category</th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider cursor-pointer" onClick={() => handleSort('status')}>Status</th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider cursor-pointer" onClick={() => handleSort('priority')}>Priority</th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider cursor-pointer" onClick={() => handleSort('sprint')}>Sprint</th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider cursor-pointer" onClick={() => handleSort('dueAt')}>Due Date</th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider">Assignee</th>
              <th className="px-4 py-2 text-left text-[11px] font-medium text-succession-text-secondary uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-succession-bg-primary divide-y divide-succession-border">
            {items.map((it) => (
              <tr key={it.id} className={`hover:bg-succession-hover-bg ${selectedItems.includes(it.id) ? 'bg-succession-primary-10' : ''}`}>
                <td className="px-4 py-2"><input type="checkbox" checked={selectedItems.includes(it.id)} onChange={() => toggleOne(it.id)} /></td>
                <td className="px-4 py-2">
                  {onItemClick ? (
                    <button className="text-left text-succession-text-primary hover:text-succession-primary hover:underline transition-colors" onClick={(e) => { e.preventDefault(); e.stopPropagation(); console.log('MapTable onItemClick called', it.id); onItemClick(it); }}>{it.title}</button>
                  ) : (
                    <input className="w-full border border-succession-border bg-succession-bg-primary text-succession-text-primary rounded-md px-2 py-1 text-sm" defaultValue={it.title} onBlur={(e) => onItemUpdate && onItemUpdate(it.id, { title: e.target.value })} />
                  )}
                </td>
                <td className="px-4 py-2">
                  {(() => {
                    const d = it.domain || it.taskTemplate?.domain;
                    return (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${getDomainClasses(d)}`}>
                        {startCase(d)}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-2">
                  {(() => {
                    const v = it.cepaCategory || it.taskTemplate?.cepaCategory;
                    return (
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${getVmiClasses(v)}`}>
                        {startCase(v)}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                    it.status === 'completed' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                    it.status === 'in_progress' ? 'bg-succession-primary-10 text-succession-primary border-blue-200' :
                    it.status === 'blocked' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-succession-bg-secondary text-succession-text-secondary border-gray-200'
                  }`}>
                    {humanize(it.status)}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] border ${
                    it.priority === 'high' ? 'bg-orange-50 text-orange-700 border-orange-200' :
                    it.priority === 'low' ? 'bg-succession-bg-secondary text-succession-text-secondary border-gray-200' : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                  }`}>
                    {it.priority || '—'}
                  </span>
                </td>
                <td className="px-4 py-2">
                  {(() => {
                    const names = it.sprintNames || [];
                    const ids = Array.isArray(it.sprintIds) ? it.sprintIds : [];
                    if (!names.length) return <span className="text-succession-text-muted text-sm">—</span>;
                    return (
                      <div className="flex flex-wrap gap-1">
                        {names.map((n, idx) => {
                          const sid = ids[idx];
                          const href = sid ? `/succession/sprint?sprintId=${encodeURIComponent(sid)}` : '/succession/sprint';
                          return (
                            <a
                              key={`${it.id}-sp-${idx}`}
                              href={href}
                              className="text-[11px] rounded-full border px-2 py-0.5 bg-succession-bg-secondary text-succession-text-secondary hover:text-succession-primary hover:border-succession-primary"
                            >
                              {n}
                            </a>
                          );
                        })}
                      </div>
                    );
                  })()}
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm ${it.dueAt && new Date(it.dueAt) < new Date() && it.status !== 'completed' ? 'text-red-600 font-medium' : 'text-succession-text-secondary'}`}>
                      {it.dueAt ? new Date(it.dueAt).toLocaleDateString() : '—'}
                    </span>
                    {it.taskTemplate?.deadlineInDays && (
                      <span className="text-[11px] text-succession-text-muted">({it.taskTemplate.deadlineInDays}d)</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span className="text-sm text-succession-text-secondary">{it.assigneeUserId || 'Unassigned'}</span>
                </td>
                <td className="px-4 py-2 text-xs text-succession-text-secondary">
                  <button className="px-2 py-1 border border-succession-border rounded text-succession-text-primary" onClick={() => onItemClick && onItemClick(it)}>View Details</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MapTable;


